export const ROUTES = {
  home: "/",
  login: "/login",
  fullLogin: "/full-login",
  signup: "/signup",
  signupMgbhp: "/mgbhp",
  signupEmblem: "/emblememployees",
  signupEmblemILS: "/emblemhealth",
  signupBeanstalk: "/beanstalk",
  signupQuickJoin: "/quick-join",
  signupActivityPartnership: "/activity-partnership",
  intakeForm: "/intake-form",
  users: "/users",
  emailSent: "/email-sent",
  emailVerified: "/email-verified",
  pendingVerification: "/pending-verification",
  codeValidation: "/code-validation",
  magicLink: "/magic-link",
  notFound: "/not-found",
  maintenance: "/maintenance",
  hubHome: "/home",

  hubBookVisit: "/book-visit",
  hubMeetClinician: "/book-visit/meet-clinician",
  hubMeetProviderSelection: "/book-visit/meet-provider/provider-selection",
  hubMeetProviderAppointments: "/book-visit/meet-provider/appointments",
  hubMeetProviderConfirmed: "/book-visit/meet-provider/visit-confirmed",
  hubMeetProviderPatientInformation:
    "/book-visit/meet-provider/patient-information",
  hubMeetCoach: "/book-visit/meet-coach",
  hubCoachPricing: "/book-visit/meet-coach/pricing",
  hubMoreInformation: "/book-visit/more-information",
  hubCoach: "/coach",
  hubResources: "/resources",
  hubFaq: "/faq",
  hubContactUs: "/contact",
  hubProfile: "/profile",
  hubCancelVisit: "/visit/:appointmentId/cancel",

  getStarted: "/get-started",
  getStartedInsurance: "/get-started/insurance",
  getStartedGoal: "/get-started/goal",
  getStartedOrientingInfo: "/get-started/orienting-info",
  getStartedJoinWaitlist: "/get-started/join-waitlist",
  getStartedJoinedWaitlist: "/get-started/joined-waitlist",
  getStartedNonValidInsurance: "/get-started/non-valid-insurance",
  schedulingDemographics: "/demographics",
  schedulingInitialVisit: "/initial-visit",
  schedulingPatientInformation: "/patient-information",
  schedulingEmailInput: "/email-input",
  schedulingVisitConfirmed: "/visit-confirmed",

  // Policy routes
  privacyPolicy: "/consent/privacy-policy",
  patientAgreement: "/consent/patient-agreement",
  privacyPractices: "/consent/privacy-practices",
  consumerHealthData: "/consent/consumer-health-data",
  termsOfUse: "/consent/terms",

  // Admin routes
  dashboard: "/dashboard",
  adminProviderDashboard: "/admin/provider-dashboard",
  adminBannerManagement: "/admin/banners",
  adminInsuranceProviders: "/admin/insurance-providers",
  adminInsuranceProviderDetail: "/admin/insurance-providers/:providerId",
  adminUserEvents: "/admin/user-events",
} as const;

export const MODAL_ROUTES = {
  userDetails: "/user-details",
  addUser: "/add-user",
  changeRole: "/change-role",
  staffBooking: "/staff-booking",
  cancelAVisit: "/cancel-a-visit",
  editCancellation: "/edit-cancellation",
  providerDetails: `${ROUTES.adminProviderDashboard}/provider-details`,
  editProviderSchedule: `${ROUTES.adminProviderDashboard}/edit-provider-schedule`,
  bannerDetails: `${ROUTES.adminBannerManagement}/banner-details`,
  activityDetails: `${ROUTES.adminUserEvents}/activity-details`,
} as const;

export type Route = (typeof ROUTES)[keyof typeof ROUTES];

const ROUTE_VALUES = Object.values(ROUTES) as readonly Route[];

export const isRoute = (value: string): value is Route => {
  return (ROUTE_VALUES as readonly string[]).includes(value);
};

const MODAL_ROUTE_VALUES = Object.values(MODAL_ROUTES);

// Lets a modal route (e.g. an ActivityDetailModal share link) open when its
// URL is loaded directly - not only when navigated to internally via
// useNavigateModal (which carries a `previousLocation` in router state).
export const isModalRoute = (pathname: string): boolean => {
  return MODAL_ROUTE_VALUES.some((route) => pathname.startsWith(route));
};

// Parent screen for each modal route when there's no real SPA history to
// fall back to (cold load / shared link - no `previousLocation`). Modals
// built by nesting under a ROUTES entry (e.g.
// `${ROUTES.adminUserEvents}/activity-details`) get their parent derived
// automatically below. The remaining modals aren't nested under any ROUTES
// entry, so there's nothing to derive from - they're hand-mapped here since
// they all belong to the Users management flow reachable from the Users
// dashboard.
const MODAL_PARENT_OVERRIDES: Partial<
  Record<keyof typeof MODAL_ROUTES, Route>
> = {
  userDetails: ROUTES.dashboard,
  addUser: ROUTES.dashboard,
  changeRole: ROUTES.dashboard,
  staffBooking: ROUTES.dashboard,
  cancelAVisit: ROUTES.dashboard,
};

// Resolves which top-level screen a given modal route belongs to, so a
// cold-loaded modal (no internal history to pop) can close into - or render
// its background as - somewhere sane instead of a no-op or a 404 page.
export const getModalParentRoute = (pathname: string): Route => {
  const modalKey = (
    Object.keys(MODAL_ROUTES) as (keyof typeof MODAL_ROUTES)[]
  ).find((key) => pathname.startsWith(MODAL_ROUTES[key]));

  if (!modalKey) return ROUTES.dashboard;

  const override = MODAL_PARENT_OVERRIDES[modalKey];
  if (override) return override;

  const derivedParent = ROUTE_VALUES.find((route) =>
    MODAL_ROUTES[modalKey].startsWith(`${route}/`),
  );

  return derivedParent ?? ROUTES.dashboard;
};

const DEEP_LINK_ROUTES = new Set<string>([
  ROUTES.hubBookVisit,
  ROUTES.hubMeetCoach,
  ROUTES.hubMeetClinician,
  ROUTES.hubCoach,
]);

export const isDeepLinkRoute = (pathname: string): boolean => {
  return DEEP_LINK_ROUTES.has(pathname);
};
